import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Monitor } from 'lucide-react';
import { useT } from '../../i18n';
import { useDevices } from '../../api';
import { usePermission } from '../../auth/permissions';
import { useAuthStore } from '../../auth/store';
import { SearchField, StatusDot } from '../../ui';
import { NAV_ITEMS } from '../nav';
import s from './GlobalSearch.module.css';

/** Recherche globale : pages de la console + appareils (raccourci « / » ou Ctrl/⌘+K). */
export function GlobalSearch() {
  const { t } = useT();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const permissions = useAuthStore((x) => x.user?.memberships.find((m) => m.orgId === x.orgId)?.permissions ?? []);
  const canDevices = usePermission('devices.read');

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest('input,textarea,select,[contenteditable]');
      if ((e.key === 'k' && (e.ctrlKey || e.metaKey)) || (e.key === '/' && !typing)) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, []);

  const devices = useDevices({ q: debounced, pageSize: 5 }, { enabled: canDevices && debounced.length >= 2 });

  const results = useMemo(() => {
    const needle = debounced.toLowerCase();
    if (needle.length < 1) return [];
    const pages = NAV_ITEMS.filter((n) => !n.permission || [n.permission].flat().some((p) => permissions.includes(p)))
      .filter((n) => t(n.labelKey).toLowerCase().includes(needle))
      .map((n) => ({ key: `page-${n.id}`, kind: 'page' as const, label: t(n.labelKey), path: n.path, icon: <n.icon size={16} /> }));
    const devs =
      canDevices && debounced.length >= 2
        ? (devices.data?.items ?? []).map((d) => ({
            key: `dev-${d.id}`,
            kind: 'device' as const,
            label: d.name,
            path: `/fleet/${d.id}`,
            icon: <Monitor size={16} />,
            status: d.status,
          }))
        : [];
    return [...pages, ...devs];
  }, [debounced, devices.data, canDevices, permissions, t]);

  const go = (path: string) => {
    setOpen(false);
    setQ('');
    navigate(path);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && results[active]) {
      go(results[active].path);
    } else if (e.key === 'Escape') {
      setOpen(false);
      input.current?.blur();
    }
  };

  return (
    <div className={s.root} ref={root}>
      <SearchField
        ref={input}
        value={q}
        onValueChange={(v) => {
          setQ(v);
          setOpen(true);
          setActive(0);
        }}
        label={t('search.placeholder')}
        placeholder={t('search.placeholder')}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        aria-controls={open ? listId : undefined}
        aria-expanded={open && debounced.length > 0}
        aria-autocomplete="list"
        role="combobox"
      />
      <kbd className={s.kbd} aria-hidden="true">
        /
      </kbd>
      {open && debounced.length > 0 && (
        <div className={s.panel} id={listId} role="listbox" aria-label={t('search.results')}>
          {results.length === 0 && <p className={s.none}>{t('search.none')}</p>}
          {results.map((r, i) => (
            <button
              key={r.key}
              type="button"
              role="option"
              aria-selected={i === active}
              className={s.option}
              data-active={i === active || undefined}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(r.path)}
            >
              {r.icon}
              <span className={s.label}>{r.label}</span>
              {r.kind === 'device' ? <StatusDot status={r.status} label={t(r.status === 'online' ? 'status.online' : 'status.offline')} /> : <span className={s.kind}>{t('search.page')}</span>}
              {i === active && <CornerDownLeft size={14} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
